/**
 * AppModal - Global application modal using Dialog from ui
 * Integrates with ModalSlice state for dynamic content
 */
import { LitheFC, LitheSpecificElement } from '@wheelbox/lithejs-engine';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  closeDialog,
} from '../ui/dialog';

import { createId } from '../../utils';
import { withSlice, subscribe, unsubscribe } from '../../state';

import { ModalSlice } from '../../state/index.model';

type AppModalProps = {
  slice: string;
};

/**
 * AppModal - A global modal that reads title/content from state slice
 */
const AppModal: LitheFC<AppModalProps> = ({
  id = createId(),
  testId = 'app-modal',
  slice,
}) => {
  const state = withSlice<ModalSlice>(slice);

  const onMountHandler = () => {
    subscribe(slice, id);
  };

  const onUnmountHandler = () => {
    unsubscribe(slice, id);
  };

  const handleClose = () => {
    closeDialog(`${id}-content`);
  };

  // Resolve content - can be LitheFC or LitheSpecificElement
  const resolvedContent: LitheSpecificElement | string =
    typeof state?.content === 'function'
      ? state.content({})
      : state?.content ?? '';

  return Dialog(
    {
      id,
      testId,
      onmount: onMountHandler,
      onunmount: onUnmountHandler,
    },
    DialogContent(
      {
        id: `${id}-dialog`,
        dialogId: id,
        classes: 'max-h-[80vh] overflow-auto',
        onClose: handleClose,
      },
      DialogHeader(
        { id: `${id}-header` },
        DialogTitle({ id: `${id}-title` }, state?.title ?? '')
      ),
      ['div', { id: `${id}-body`, 'data-slot': 'dialog-body' }, resolvedContent]
    )
  );
};

export default AppModal;
